import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect on auth endpoints — let the component handle it
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/google') || url.includes('/auth/yandex') || url.includes('/auth/vk') || url.includes('/auth/forgot-password') || url.includes('/auth/reset-password');
      if (!isAuthEndpoint) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (accessToken) => api.post('/auth/google', { access_token: accessToken }),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  setPassword: (data) => api.post('/auth/set-password', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  deleteAccount: (data) => api.delete('/auth/account', { data }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Form Schemas
export const formSchemaAPI = {
  getAll: () => api.get('/form-schemas'),
  getOne: (id) => api.get(`/form-schemas/${id}`),
  create: (data) => api.post('/form-schemas', data),
  update: (id, data) => api.put(`/form-schemas/${id}`, data),
  delete: (id) => api.delete(`/form-schemas/${id}`),
  getTemplates: () => api.get('/form-templates'),
};

// Orders
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getOne: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
};

// Customers
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Materials
export const materialsAPI = {
  getAll: (params) => api.get('/materials', { params }),
  getOne: (id) => api.get(`/materials/${id}`),
  create: (data) => api.post('/materials', data),
  update: (id, data) => api.put(`/materials/${id}`, data),
  delete: (id) => api.delete(`/materials/${id}`),
};

export const materialCategoriesAPI = {
  getAll: () => api.get('/material-categories'),
  create: (data) => api.post('/material-categories', data),
  update: (id, data) => api.put(`/material-categories/${id}`, data),
  delete: (id) => api.delete(`/material-categories/${id}`),
};

// Tasks
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  exportIcal: () => api.get('/tasks/export/ical', { responseType: 'blob' }),
  exportTaskIcal: (id) => api.get(`/tasks/${id}/ical`, { responseType: 'blob' }),
  getCalendarToken: () => api.get('/me/calendar-token'),
};

// Dashboard
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
};

// Calendar
export const calendarAPI = {
  getEvents: (year, month, startDate, endDate) => {
    const params = { year, month };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/calendar/events', { params });
  },
};

// Reports
export const reportsAPI = {
  exportOrdersCSV: () => api.get('/reports/orders-csv', { responseType: 'blob' }),
  exportTasksCSV: () => api.get('/reports/tasks-csv', { responseType: 'blob' }),
  exportCustomersCSV: () => api.get('/reports/customers-csv', { responseType: 'blob' }),
  exportTasksICS: () => api.get('/reports/ics', { responseType: 'blob' }),
};

// Finance
export const financeAPI = {
  getCategories: () => api.get('/finance/categories'),
  createCategory: (data) => api.post('/finance/categories', data),
  updateCategory: (id, data) => api.put(`/finance/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/finance/categories/${id}`),
  getTransactions: (params) => api.get('/finance/transactions', { params }),
  createTransaction: (data) => api.post('/finance/transactions', data),
  updateTransaction: (id, data) => api.put(`/finance/transactions/${id}`, data),
  deleteTransaction: (id) => api.delete(`/finance/transactions/${id}`),
  getSummary: (params) => api.get('/finance/summary', { params }),
  getChartData: () => api.get('/finance/chart-data'),
};

// Files
export const filesAPI = {
  upload: (file, { timeout = 60000, retries = 1 } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const attempt = (retriesLeft) =>
      api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout,
      }).catch((err) => {
        if (retriesLeft > 0) {
          return new Promise((resolve) => setTimeout(resolve, 1000)).then(() =>
            attempt(retriesLeft - 1)
          );
        }
        throw err;
      });
    return attempt(retries);
  },
  delete: (fileId) => api.delete(`/upload/${fileId}`),
};

// Settings
export const settingsAPI = {
  getNotifications: () => api.get('/settings/notifications'),
  updateNotifications: (data) => api.put('/settings/notifications', data),
  getOrderNumber: () => api.get('/settings/order-number'),
  updateOrderNumber: (data) => api.put('/settings/order-number', data),
};

// Telegram
export const telegramAPI = {
  generateLink: () => api.post('/telegram/generate-link'),
  getLinkStatus: () => api.get('/telegram/link-status'),
  unlink: () => api.post('/telegram/unlink'),
  testMessage: () => api.post('/telegram/test-message'),
};

// Email
export const emailAPI = {
  testMessage: () => api.post('/email/test-message'),
};

// Push Notifications
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-public-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
  testMessage: () => api.post('/push/test'),
};

// Admin Database
export const adminDbAPI = {
  getStats: () => api.get('/admin/db/stats'),
  getCollections: () => api.get('/admin/db/collections'),
  createCollection: (name) => api.post('/admin/db/collections', { name }),
  dropCollection: (name) => api.delete(`/admin/db/collections/${name}`),
  getDocuments: (col, params) => api.get(`/admin/db/collections/${col}/documents`, { params }),
  getDocument: (col, id) => api.get(`/admin/db/collections/${col}/document/${id}`),
  updateDocument: (col, id, data) => api.put(`/admin/db/collections/${col}/document/${id}`, data),
  deleteDocument: (col, id) => api.delete(`/admin/db/collections/${col}/document/${id}`),
  createDocument: (col, data) => api.post(`/admin/db/collections/${col}/document`, data),
  runQuery: (data) => api.post('/admin/db/query', data),
  exportCollection: (col) => api.get(`/admin/db/export/${col}`, { responseType: 'blob' }),
  importCollection: (col, documents) => api.post(`/admin/db/import/${col}`, { documents }),
  exportAll: () => api.get('/admin/db/export-all', { responseType: 'blob' }),
  importAll: (file, clearExisting = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('clear_existing', clearExisting.toString());
    return api.post('/admin/db/import-all', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Subscription
export const subscriptionAPI = {
  getCurrent: () => api.get('/subscription'),
  getPlans: () => api.get('/subscription/plans'),
  checkLimit: (resourceType) => api.get(`/subscription/check/${resourceType}`),
};

// Payment (Tbank)
export const paymentAPI = {
  init: (data) => api.post('/payment/init', data),
  getStatus: (paymentId) => api.get(`/payment/status/${paymentId}`),
  getHistory: () => api.get('/payment/history'),
};

// Admin Tbank Settings
export const adminTbankAPI = {
  get: () => api.get('/admin/tbank-settings'),
  update: (data) => api.put('/admin/tbank-settings', data),
  test: () => api.post('/admin/tbank-settings/test'),
  getPayments: () => api.get('/admin/payments'),
};

// Admin Subscription
export const adminSubscriptionAPI = {
  get: (userId) => api.get(`/admin/users/${userId}/subscription`),
  activate: (userId, data) => api.post(`/admin/users/${userId}/subscription`, data),
  cancel: (userId) => api.delete(`/admin/users/${userId}/subscription`),
};

export const publicPageAPI = {
  get: () => api.get('/public-page'),
  update: (data) => api.put('/public-page', data),
  checkSlug: (slug) => api.get(`/public-page/check-slug/${slug}`),
  deletePhoto: (url) => api.delete('/public-page/photo', { data: { url } }),
  getPublic: (slug) => api.get(`/page-data/${slug}`),
};

// Captcha
export const captchaAPI = {
  status: () => api.get('/captcha/status'),
  generate: () => api.get('/captcha/generate'),
};

// Admin Captcha
export const adminCaptchaAPI = {
  get: () => api.get('/admin/captcha'),
  update: (data) => api.put('/admin/captcha', data),
};

// News
export const newsAPI = {
  getAll: () => api.get('/news'),
  markRead: () => api.post('/news/mark-read'),
};

// Admin News
export const adminNewsAPI = {
  getAll: () => api.get('/admin/news'),
  create: (data) => api.post('/admin/news', data),
  update: (id, data) => api.put(`/admin/news/${id}`, data),
  delete: (id) => api.delete(`/admin/news/${id}`),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getPublic: (publicId) => api.get(`/invoices/public/${publicId}`),
  batchPay: (invoiceIds) => api.put('/invoices/batch-pay', { invoice_ids: invoiceIds }),
  batchDelete: (invoiceIds) => api.delete('/invoices/batch-delete', { data: { invoice_ids: invoiceIds } }),
};

// Payment Settings
export const paymentSettingsAPI = {
  get: () => api.get('/settings/payment'),
  update: (data) => api.put('/settings/payment', data),
};

// Search
export const searchAPI = {
  search: (q) => api.get(`/search?q=${encodeURIComponent(q)}`),
};

export default api;
